import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import bgImageLocal from "../../assets/pictures/15.jpg";
import { useAuth } from "../../context/auth-context";

type DepartamentoAcessivel = {
  id?: number | string | null;
  nome: string;
  slug?: string | null;
  routeKey?: string | null;
  themeColor?: string | null;
  iconKey?: string | null;
  modulos?: any[];
};

type EmpresaAcessivel = {
  id: number | string;
  nome: string;
  label?: string | null;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  papel?: string | null;
  ambiente_padrao?: boolean | null;
  tipo_ambiente?: string | null;
};

type HomeBackgroundLicense = {
  titulo: string;
  fonte: string;
  tipoLicenca: string;
  titularLicenca: string;
  cnpjTitular: string;
  dataAquisicao: string;
  arquivoOriginal: string;
  usoAutorizado: string;
  urlOrigem?: string | null;
  urlLicenca?: string | null;
  observacoes?: string | null;
};

type GovernanceInfo = {
  sistema: string;
  produto: string;
  empresaResponsavel: string;
  cnpj: string;
  ambienteAtual: string;
  canalEmail: string;
  canalTelefone: string;
};

type AddressOption = {
  value: string;
  label: string;
  lines: string[];
  isAll?: boolean;
};

type CompanyOption = {
  value: string;
  label: string;
  subtitle?: string | null;
  tipoAmbiente?: string | null;
};

type SessaoEndereco = {
  id?: number | string | null;
  label?: string | null;
  nome?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
};

type SessaoEnderecoComModulos = {
  id?: number | string | null;
  departamentos_acessiveis?: any[];
};

const ALL_ADDRESSES_VALUE = "__ALL__";
const STORAGE_KEY_SELECTED_ADDRESS = "eva:leftbar:selected-address-global";
const STORAGE_KEY_SELECTED_COMPANY = "eva:home:selected-company";

const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const slugify = (s: string) =>
  norm(s)
    .replace(/&/g, "e")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const getDeptRouteKey = (dept: DepartamentoAcessivel) => {
  if (dept.routeKey && String(dept.routeKey).trim()) {
    return String(dept.routeKey).trim().replace(/^\/+/, "");
  }

  if (dept.slug && String(dept.slug).trim()) {
    return String(dept.slug).trim().replace(/^\/+/, "");
  }

  return slugify(dept.nome || "departamento");
};

const getDeptDisplayColor = (
  dept: DepartamentoAcessivel,
  index: number
): string => {
  if (dept.themeColor && String(dept.themeColor).trim()) {
    return String(dept.themeColor);
  }

  const fallbackPalette = [
    "#1565c0",
    "#f39c12",
    "#13c2c2",
    "#ff5a47",
    "#7ac800",
    "#14a9d8",
    "#7b61ff",
    "#00a86b",
  ];

  return fallbackPalette[index % fallbackPalette.length];
};

const isStatusServicoDepartment = (dept: DepartamentoAcessivel) => {
  const nome = norm(dept.nome || "");
  const slug = norm(dept.slug || "");
  const routeKey = norm(dept.routeKey || "");

  return (
    nome === "status do servico" ||
    slug === "status-servico" ||
    routeKey === "status-do-servico"
  );
};

const DeptInternetIcon: React.FC<{
  iconKey?: string | null;
  size?: number;
  color?: string;
  alt?: string;
  style?: React.CSSProperties;
}> = ({ iconKey, size = 20, color = "#ffffff", alt = "", style }) => {
  const safeKey =
    typeof iconKey === "string" && iconKey.includes(":")
      ? iconKey
      : "lucide:circle";

  const src = `https://api.iconify.design/${safeKey}.svg?color=${encodeURIComponent(
    color
  )}`;

  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      draggable={false}
      loading="eager"
      style={{
        width: size,
        height: size,
        display: "block",
        flexShrink: 0,
        userSelect: "none",
        pointerEvents: "none",
        ...style,
      }}
      onError={(e) => {
        e.currentTarget.src =
          "https://api.iconify.design/lucide:circle.svg?color=%23ffffff";
      }}
    />
  );
};

const InfoField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  return (
    <div style={styles.infoField}>
      <div style={styles.infoFieldLabel}>{label}</div>
      <div style={styles.infoFieldValue}>{value}</div>
    </div>
  );
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { sessao, trocarEmpresa, switchingEmpresa } = useAuth();

  const [developmentModule, setDevelopmentModule] =
    useState<DepartamentoAcessivel | null>(null);

  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [governanceModalOpen, setGovernanceModalOpen] = useState(false);

  const [selectedCompanyValue, setSelectedCompanyValue] = useState<string>("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);

  const [selectedAddressValue, setSelectedAddressValue] = useState<string>("");
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);

  const bgImage = bgImageLocal;

  const backgroundLicense = useMemo<HomeBackgroundLicense>(
    () => ({
      titulo: "Imagem institucional marítima - Home Logvi",
      fonte: "Magnific / Freepik",
      tipoLicenca:
        "Assinatura Premium com uso comercial, conforme termos vigentes da plataforma",
      titularLicenca: "HAVOX Desenvolvimento Tecnológico Ltda",
      cnpjTitular: "57.588.843/0001-78",
      dataAquisicao: "07/05/2026",
      arquivoOriginal: "15.jpg",
      usoAutorizado:
        "Registro interno de uso como plano de fundo da interface Home do sistema Logvi, em ambiente corporativo.",
      urlOrigem: null,
      urlLicenca: null,
      observacoes:
        "Os direitos de uso decorrem da assinatura ativa e dos termos aplicáveis da plataforma na data de aquisição/download. Registro mantido para rastreabilidade interna.",
    }),
    []
  );

  const governanceInfo = useMemo<GovernanceInfo>(
    () => ({
      sistema: "Logvi/EVA",
      produto: "Plataforma corporativa de gestão, operação e rastreabilidade",
      empresaResponsavel: "HAVOX Desenvolvimento Tecnológico Ltda",
      cnpj: "57.588.843/0001-78",
      ambienteAtual:
        String((sessao as any)?.empresa_tipo_ambiente || "").toLowerCase() ===
        "desenvolvimento"
          ? "Desenvolvimento"
          : "Produção",
      canalEmail: "service@havox.com.br",
      canalTelefone: "(21) 96427-8076",
    }),
    [sessao]
  );

  const displayName = useMemo(() => {
    const clean = String(sessao?.nome || "").trim();
    return clean || "Usuário";
  }, [sessao?.nome]);

  const activeCompanyName = useMemo(() => {
    const possibleCompany =
      (sessao as any)?.empresa_nome ||
      (sessao as any)?.empresa_nome_fantasia ||
      (sessao as any)?.empresa?.nome ||
      (sessao as any)?.empresa?.nome_fantasia ||
      (sessao as any)?.empresa?.razao_social ||
      "";

    return String(possibleCompany || "").trim();
  }, [sessao]);

  const empresasAcessiveis = useMemo<EmpresaAcessivel[]>(() => {
    const raw = (sessao as any)?.empresas_acessiveis;

    if (!Array.isArray(raw)) return [];

    return raw
      .map((empresa: any) => ({
        id: empresa?.id,
        nome: String(
          empresa?.nome || empresa?.nome_fantasia || empresa?.razao_social || ""
        ),
        label:
          empresa?.label !== undefined && empresa?.label !== null
            ? String(empresa.label)
            : null,
        razao_social:
          empresa?.razao_social !== undefined && empresa?.razao_social !== null
            ? String(empresa.razao_social)
            : null,
        nome_fantasia:
          empresa?.nome_fantasia !== undefined &&
          empresa?.nome_fantasia !== null
            ? String(empresa.nome_fantasia)
            : null,
        cnpj:
          empresa?.cnpj !== undefined && empresa?.cnpj !== null
            ? String(empresa.cnpj)
            : null,
        papel:
          empresa?.papel !== undefined && empresa?.papel !== null
            ? String(empresa.papel)
            : null,
        ambiente_padrao: empresa?.ambiente_padrao === true,
        tipo_ambiente:
          empresa?.tipo_ambiente !== undefined &&
          empresa?.tipo_ambiente !== null
            ? String(empresa.tipo_ambiente)
            : null,
      }))
      .filter(
        (empresa) =>
          empresa.id !== undefined &&
          empresa.id !== null &&
          String(empresa.id).trim().length > 0
      );
  }, [sessao]);

  const companyOptions = useMemo<CompanyOption[]>(() => {
    return empresasAcessiveis.map((empresa) => {
      const tipo = String(empresa.tipo_ambiente || "").toLowerCase();

      const fallbackLabel =
        empresa.label ||
        empresa.nome_fantasia ||
        empresa.nome ||
        empresa.razao_social ||
        `Empresa ${empresa.id}`;

      const ambienteLabel =
        tipo === "desenvolvimento"
          ? "Ambiente de desenvolvimento"
          : tipo === "homologacao"
          ? "Ambiente de homologação"
          : "Ambiente de produção";

      return {
        value: String(empresa.id),
        label: String(fallbackLabel),
        subtitle: ambienteLabel,
        tipoAmbiente: tipo || null,
      };
    });
  }, [empresasAcessiveis]);

  const defaultCompanyValue = useMemo(() => {
    const ativa =
      (sessao as any)?.empresa_ativa_id || (sessao as any)?.empresa_id || null;

    if (ativa !== null && ativa !== undefined) {
      return String(ativa);
    }

    const padrao = empresasAcessiveis.find(
      (empresa) => empresa.ambiente_padrao === true
    );

    if (padrao?.id !== undefined && padrao?.id !== null) {
      return String(padrao.id);
    }

    return companyOptions[0]?.value || "";
  }, [sessao, empresasAcessiveis, companyOptions]);

  const selectedCompanyOption = useMemo(() => {
    const value = selectedCompanyValue || defaultCompanyValue;
    return companyOptions.find((opt) => opt.value === value) || null;
  }, [companyOptions, selectedCompanyValue, defaultCompanyValue]);

  const mapDepartmentFromApi = (dept: any): DepartamentoAcessivel => ({
    id: dept?.id ?? null,
    nome: String(dept?.nome || ""),
    slug:
      dept?.slug !== undefined && dept?.slug !== null
        ? String(dept.slug)
        : null,
    routeKey:
      dept?.routeKey !== undefined && dept?.routeKey !== null
        ? String(dept.routeKey)
        : dept?.route_key !== undefined && dept?.route_key !== null
        ? String(dept.route_key)
        : null,
    themeColor:
      dept?.themeColor !== undefined && dept?.themeColor !== null
        ? String(dept.themeColor)
        : dept?.theme_color !== undefined && dept?.theme_color !== null
        ? String(dept.theme_color)
        : null,
    iconKey:
      dept?.iconKey !== undefined && dept?.iconKey !== null
        ? String(dept.iconKey)
        : dept?.icon_key !== undefined && dept?.icon_key !== null
        ? String(dept.icon_key)
        : null,
    modulos: Array.isArray(dept?.modulos) ? dept.modulos : [],
  });

  const cleanDepartments = (raw: any[]): DepartamentoAcessivel[] => {
    return raw
      .map(mapDepartmentFromApi)
      .filter((dept) => dept.nome.trim().length > 0);
  };

  const mergeDepartments = (departmentLists: DepartamentoAcessivel[][]) => {
    const depMap = new Map<string, DepartamentoAcessivel>();

    for (const list of departmentLists) {
      for (const dept of list || []) {
        const key = norm(getDeptRouteKey(dept));
        const existing = depMap.get(key);

        if (!existing) {
          depMap.set(key, {
            ...dept,
            modulos: Array.isArray(dept.modulos) ? [...dept.modulos] : [],
          });
          continue;
        }

        const moduleMap = new Map<string, any>();

        for (const mod of existing.modulos || []) {
          const modKey = String(mod?.codigo || mod?.label || mod?.id || "");
          if (modKey) moduleMap.set(modKey, mod);
        }

        for (const mod of dept.modulos || []) {
          const modKey = String(mod?.codigo || mod?.label || mod?.id || "");
          if (modKey && !moduleMap.has(modKey)) moduleMap.set(modKey, mod);
        }

        depMap.set(key, {
          ...existing,
          themeColor: existing.themeColor || dept.themeColor,
          iconKey: existing.iconKey || dept.iconKey,
          modulos: Array.from(moduleMap.values()),
        });
      }
    }

    return Array.from(depMap.values());
  };

  const sessaoEnderecos = useMemo<SessaoEndereco[]>(() => {
    return Array.isArray((sessao as any)?.enderecos_disponiveis)
      ? ((sessao as any).enderecos_disponiveis as SessaoEndereco[])
      : [];
  }, [sessao]);

  const addressOptions = useMemo<AddressOption[]>(() => {
    const mapped = sessaoEnderecos.map((end) => {
      const nome =
        typeof end?.nome === "string" && end.nome.trim() ? end.nome.trim() : "";

      const linhaPrincipal = [end?.logradouro, end?.numero]
        .filter((x) => typeof x === "string" && String(x).trim().length > 0)
        .map((x) => String(x).trim())
        .join(", ");

      const linhaSecundaria = [end?.bairro, end?.cidade, end?.uf]
        .filter((x) => typeof x === "string" && String(x).trim().length > 0)
        .map((x) => String(x).trim())
        .join(" - ");

      const fallbackLabel =
        typeof end?.label === "string" && end.label.trim()
          ? end.label.trim()
          : [nome, linhaPrincipal, linhaSecundaria]
              .filter(Boolean)
              .join(" • ") || `Endereço ${String(end?.id ?? "")}`;

      const lines = [nome, linhaPrincipal, linhaSecundaria].filter(
        (x) => typeof x === "string" && x.trim().length > 0
      );

      return {
        value: String(end?.id ?? ""),
        label: fallbackLabel,
        lines: lines.length > 0 ? lines : [fallbackLabel],
      } as AddressOption;
    });

    return [
      {
        value: ALL_ADDRESSES_VALUE,
        label: "Todos os endereços",
        lines: ["Todos os endereços"],
        isAll: true,
      },
      ...mapped,
    ];
  }, [sessaoEnderecos]);

  const defaultAddressValue = useMemo(() => {
    if (
      (sessao as any)?.endereco_selecionado_id !== undefined &&
      (sessao as any)?.endereco_selecionado_id !== null
    ) {
      return String((sessao as any).endereco_selecionado_id);
    }

    const firstRealAddress = addressOptions.find((opt) => !opt.isAll);
    return firstRealAddress?.value || ALL_ADDRESSES_VALUE;
  }, [sessao, addressOptions]);

  const baseDepartments = useMemo<DepartamentoAcessivel[]>(() => {
    const raw: any[] = Array.isArray(sessao?.departamentos_acessiveis)
      ? sessao?.departamentos_acessiveis ?? []
      : [];

    return cleanDepartments(raw);
  }, [sessao?.departamentos_acessiveis]);

  const departmentsByAddress = useMemo(() => {
    const map = new Map<string, DepartamentoAcessivel[]>();

    const rows: SessaoEnderecoComModulos[] = Array.isArray(
      (sessao as any)?.enderecos_com_modulos
    )
      ? ((sessao as any).enderecos_com_modulos as SessaoEnderecoComModulos[])
      : [];

    rows.forEach((row) => {
      const key = String(row?.id ?? "");
      const deps = Array.isArray(row?.departamentos_acessiveis)
        ? cleanDepartments(row.departamentos_acessiveis)
        : [];

      map.set(key, deps);
    });

    return map;
  }, [sessao]);

  const departmentsFromAllAddresses = useMemo<DepartamentoAcessivel[]>(() => {
    const lists = Array.from(departmentsByAddress.values()).filter(
      (deps): deps is DepartamentoAcessivel[] =>
        Array.isArray(deps) && deps.length > 0
    );

    if (!lists.length) return baseDepartments;

    return mergeDepartments(lists);
  }, [departmentsByAddress, baseDepartments]);

  const getSelectedAddressValue = () => {
    return selectedAddressValue || defaultAddressValue || ALL_ADDRESSES_VALUE;
  };

  const selectedAddressOption = useMemo(() => {
    const value =
      selectedAddressValue || defaultAddressValue || ALL_ADDRESSES_VALUE;
    return addressOptions.find((opt) => opt.value === value) || null;
  }, [addressOptions, selectedAddressValue, defaultAddressValue]);

  const departments = useMemo<DepartamentoAcessivel[]>(() => {
    const selected = getSelectedAddressValue();

    if (selected === ALL_ADDRESSES_VALUE) {
      return departmentsFromAllAddresses;
    }

    return (
      departmentsByAddress.get(String(selected)) || departmentsFromAllAddresses
    );
  }, [
    selectedAddressValue,
    defaultAddressValue,
    departmentsByAddress,
    departmentsFromAllAddresses,
  ]);

  const handleCompanyChange = async (empresaId: string) => {
    const current =
      selectedCompanyValue ||
      defaultCompanyValue ||
      String(
        (sessao as any)?.empresa_ativa_id || (sessao as any)?.empresa_id || ""
      );

    if (!empresaId || empresaId === current || switchingEmpresa) {
      setCompanyDropdownOpen(false);
      return;
    }

    try {
      window.localStorage.setItem(
        STORAGE_KEY_SELECTED_COMPANY,
        JSON.stringify(empresaId)
      );

      window.localStorage.removeItem(STORAGE_KEY_SELECTED_ADDRESS);

      await trocarEmpresa(empresaId);

      setSelectedCompanyValue(empresaId);
      setSelectedAddressValue("");
      setCompanyDropdownOpen(false);
      setAddressDropdownOpen(false);
    } catch (err) {
      console.error("SWITCH_COMPANY_ERROR", err);

      window.localStorage.removeItem(STORAGE_KEY_SELECTED_COMPANY);

      alert(
        err instanceof Error
          ? err.message
          : "Não foi possível trocar o ambiente."
      );
    }
  };

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY_SELECTED_COMPANY);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (typeof parsed === "string" && parsed.trim()) {
        setSelectedCompanyValue(parsed);
      }
    } catch {
      // storage inválido: ignora
    }
  }, []);

  useEffect(() => {
    if (!selectedCompanyValue && defaultCompanyValue) {
      setSelectedCompanyValue(defaultCompanyValue);
    }
  }, [defaultCompanyValue, selectedCompanyValue]);

  useEffect(() => {
    const selectedExists = companyOptions.some(
      (opt) => opt.value === selectedCompanyValue
    );

    if (!selectedExists && companyOptions.length > 0) {
      setSelectedCompanyValue(defaultCompanyValue || companyOptions[0].value);
    }
  }, [companyOptions, defaultCompanyValue, selectedCompanyValue]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY_SELECTED_ADDRESS);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (typeof parsed === "string" && parsed.trim()) {
        setSelectedAddressValue(parsed);
      }
    } catch {
      // storage inválido: ignora
    }
  }, []);

  useEffect(() => {
    try {
      if (!selectedAddressValue) return;

      window.localStorage.setItem(
        STORAGE_KEY_SELECTED_ADDRESS,
        JSON.stringify(selectedAddressValue)
      );
    } catch {
      // erro de storage: ignora
    }
  }, [selectedAddressValue]);

  useEffect(() => {
    if (!selectedAddressValue) {
      setSelectedAddressValue(defaultAddressValue || ALL_ADDRESSES_VALUE);
    }
  }, [defaultAddressValue, selectedAddressValue]);

  useEffect(() => {
    const selectedExists = addressOptions.some(
      (opt) => opt.value === selectedAddressValue
    );

    if (!selectedExists && addressOptions.length > 0) {
      setSelectedAddressValue(defaultAddressValue || ALL_ADDRESSES_VALUE);
    }
  }, [addressOptions, defaultAddressValue, selectedAddressValue]);

  useEffect(() => {
    const handleGlobalClick = () => {
      setAddressDropdownOpen(false);
      setCompanyDropdownOpen(false);
    };

    window.addEventListener("click", handleGlobalClick);

    return () => window.removeEventListener("click", handleGlobalClick);
  }, []);

  useEffect(() => {
    if (!licenseModalOpen && !governanceModalOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLicenseModalOpen(false);
        setGovernanceModalOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [licenseModalOpen, governanceModalOpen]);

  const handleDepartmentClick = (dept: DepartamentoAcessivel) => {
    if (isStatusServicoDepartment(dept)) {
      setDevelopmentModule(dept);
      return;
    }

    const routeKey = getDeptRouteKey(dept);
    navigate(`/${routeKey}`);
  };

  const closeDevelopmentMessage = () => {
    setDevelopmentModule(null);
  };

  const openExternalResource = (url?: string | null) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openLicenseModal = () => {
    setGovernanceModalOpen(false);
    setLicenseModalOpen(true);
  };

  const openGovernanceModal = () => {
    setLicenseModalOpen(false);
    setGovernanceModalOpen(true);
  };

  return (
    <div style={styles.page}>
      <div
        style={{
          ...styles.background,
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div style={styles.edgeBlur} />
      <div style={styles.topTint} />
      <div style={styles.overlay} />

      <div style={styles.content}>
        <header style={styles.customHeader}>
          <div style={styles.customHeaderLeft}>
            <div />
          </div>
        </header>

        <section style={styles.leftTopArea}>
          <div style={styles.headerBlock}>
            <h1 style={styles.title}>
              <span style={styles.titleHello}>Olá,</span>{" "}
              <span style={styles.titleName}>{displayName}!</span>
            </h1>

            <p style={styles.subtitle}>
              Em qual departamento vamos trabalhar hoje?
            </p>
          </div>

          <div style={styles.modulesRow}>
            {departments.map((dept, index) => {
              const deptColor = getDeptDisplayColor(dept, index);
              const isDevelopment = isStatusServicoDepartment(dept);

              return (
                <button
                  key={`${dept.id ?? dept.nome}-${index}`}
                  type="button"
                  style={{
                    ...styles.moduleCard,
                    background: deptColor,
                    position: "relative",
                  }}
                  onClick={() => handleDepartmentClick(dept)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow =
                      "0 14px 30px rgba(0,0,0,0.22)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px rgba(0,0,0,0.15)";
                  }}
                >
                  {isDevelopment && (
                    <span style={styles.developmentBadge}>DEV</span>
                  )}

                  <div style={styles.moduleIcon}>
                    <DeptInternetIcon
                      iconKey={dept.iconKey}
                      size={20}
                      color="#ffffff"
                      alt={dept.nome}
                    />
                  </div>

                  <div style={styles.moduleTitle}>
                    {dept.nome.split("\n").map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                </button>
              );
            })}

            {departments.length === 0 && (
              <div style={styles.emptyState}>
                Nenhum departamento disponível para esta conta.
              </div>
            )}
          </div>

          {developmentModule && (
            <div style={styles.developmentPanel}>
              <div style={styles.developmentPanelHeader}>
                <div style={styles.developmentIconBox}>
                  <DeptInternetIcon
                    iconKey={developmentModule.iconKey || "lucide:activity"}
                    size={22}
                    color="#ffffff"
                    alt={developmentModule.nome}
                  />
                </div>

                <div style={styles.developmentTextGroup}>
                  <div style={styles.developmentEyebrow}>
                    Módulo indisponível
                  </div>

                  <h2 style={styles.developmentTitle}>
                    {developmentModule.nome} está em desenvolvimento
                  </h2>
                </div>
              </div>

              <p style={styles.developmentDescription}>
                Este módulo ainda não foi liberado para operação no sistema.
                Assim que a implantação for concluída, o acesso será
                disponibilizado normalmente por esta tela.
              </p>

              <div style={styles.developmentFooter}>
                <button
                  type="button"
                  style={styles.developmentCloseButton}
                  onClick={closeDevelopmentMessage}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#26323f";
                    e.currentTarget.style.borderColor = "#4b5563";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#1f2933";
                    e.currentTarget.style.borderColor = "#374151";
                  }}
                >
                  Entendido
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* <div style={styles.bottomActions}>
        <button
          type="button"
          style={styles.governanceButton}
          onClick={openGovernanceModal}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(15, 23, 32, 0.92)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(15, 23, 32, 0.84)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
          }}
        >
          <span style={styles.actionButtonText}>Plataforma e contato</span>
        </button>

        <button
          type="button"
          style={styles.licenseButton}
          onClick={openLicenseModal}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(15, 23, 32, 0.92)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(15, 23, 32, 0.84)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
          }}
        >
          <span style={styles.licenseButtonIcon}>i</span>
          <span style={styles.actionButtonText}>Licença de uso da imagem</span>
        </button>
      </div>*/}

      {licenseModalOpen && (
        <div
          style={styles.modalBackdrop}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setLicenseModalOpen(false);
            }
          }}
        >
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalEyebrow}>Compliance de imagem</div>
                <h2 style={styles.modalTitle}>Licença de uso da imagem</h2>
              </div>

              <button
                type="button"
                style={styles.modalCloseButton}
                onClick={() => setLicenseModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div style={styles.modalDivider} />

            <div style={styles.assetSummary}>
              <div style={styles.assetThumbnailWrap}>
                <div
                  style={{
                    ...styles.assetThumbnail,
                    backgroundImage: `url(${bgImage})`,
                  }}
                />
              </div>

              <div style={styles.assetSummaryText}>
                <div style={styles.assetTitle}>{backgroundLicense.titulo}</div>
                <div style={styles.assetDescription}>
                  Imagem utilizada como plano de fundo da interface Home do
                  Logvi.
                </div>
              </div>
            </div>

            <div style={styles.sectionBlock}>
              <div style={styles.sectionTitle}>Informações da licença</div>

              <div style={styles.infoGrid}>
                <InfoField label="Fonte" value={backgroundLicense.fonte} />
                <InfoField
                  label="Tipo de licença"
                  value={backgroundLicense.tipoLicenca}
                />
                <InfoField
                  label="Titular da licença"
                  value={backgroundLicense.titularLicenca}
                />
                <InfoField
                  label="CNPJ do titular"
                  value={backgroundLicense.cnpjTitular}
                />
                <InfoField
                  label="Data de aquisição"
                  value={backgroundLicense.dataAquisicao}
                />
              </div>
            </div>

            <div style={styles.sectionBlock}>
              <div style={styles.sectionTitle}>Uso autorizado</div>
              <div style={styles.textPanel}>
                {backgroundLicense.usoAutorizado}
              </div>
            </div>

            {backgroundLicense.observacoes && (
              <div style={styles.sectionBlock}>
                <div style={styles.sectionTitle}>Observações</div>
                <div style={styles.textPanel}>
                  {backgroundLicense.observacoes}
                </div>
              </div>
            )}

            <div style={styles.modalFooter}>
              <div style={styles.footerLeftNote}>
                Registro interno de licenciamento do asset visual.
              </div>

              <div style={styles.footerActions}>
                <button
                  type="button"
                  style={{
                    ...styles.secondaryButton,
                    opacity: backgroundLicense.urlOrigem ? 1 : 0.5,
                    cursor: backgroundLicense.urlOrigem
                      ? "pointer"
                      : "not-allowed",
                  }}
                  disabled={!backgroundLicense.urlOrigem}
                  onClick={() =>
                    openExternalResource(backgroundLicense.urlOrigem)
                  }
                >
                  Ver origem
                </button>

                <button
                  type="button"
                  style={{
                    ...styles.primaryButton,
                    opacity: backgroundLicense.urlLicenca ? 1 : 0.5,
                    cursor: backgroundLicense.urlLicenca
                      ? "pointer"
                      : "not-allowed",
                  }}
                  disabled={!backgroundLicense.urlLicenca}
                  onClick={() =>
                    openExternalResource(backgroundLicense.urlLicenca)
                  }
                >
                  Ver licença
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {governanceModalOpen && (
        <div
          style={styles.modalBackdrop}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setGovernanceModalOpen(false);
            }
          }}
        >
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalEyebrow}>
                  Informações institucionais
                </div>
                <h2 style={styles.modalTitle}>Sobre o sistema</h2>
              </div>

              <button
                type="button"
                style={styles.modalCloseButton}
                onClick={() => setGovernanceModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div style={styles.modalDivider} />

            <div style={styles.sectionBlock}>
              <div style={styles.sectionTitle}>Identificação do sistema</div>

              <div style={styles.infoGrid}>
                <InfoField label="Sistema" value={governanceInfo.sistema} />
                <InfoField label="Produto" value={governanceInfo.produto} />
                <InfoField
                  label="Empresa ativa"
                  value={activeCompanyName || "Empresa vinculada à sessão"}
                />
                <InfoField
                  label="Ambiente atual"
                  value={governanceInfo.ambienteAtual}
                />
              </div>
            </div>

            <div style={styles.sectionBlock}>
              <div style={styles.sectionTitle}>Empresa responsável</div>

              <div style={styles.infoGrid}>
                <InfoField
                  label="Razão social"
                  value={governanceInfo.empresaResponsavel}
                />
                <InfoField label="CNPJ" value={governanceInfo.cnpj} />
              </div>
            </div>

            <div style={styles.sectionBlock}>
              <div style={styles.sectionTitle}>Canal de contato</div>

              <div style={styles.contactGrid}>
                <a
                  href={`mailto:${
                    governanceInfo.canalEmail
                  }?subject=${encodeURIComponent(
                    "Contato pelo canal de governança do Logvi"
                  )}&body=${encodeURIComponent(
                    "Olá,\n\nEstou entrando em contato pelo canal de governança do Logvi.\n\n"
                  )}`}
                  style={styles.contactCard}
                >
                  <div style={styles.contactLabel}>E-mail</div>

                  <div style={styles.contactValue}>
                    {governanceInfo.canalEmail}
                  </div>

                  <div style={styles.contactLinkHint}>
                    Clique para enviar um e-mail
                  </div>
                </a>

                <a
                  href={`https://wa.me/5521964278076?text=${encodeURIComponent(
                    "Olá, estou entrando em contato pelo canal de governança do Logvi."
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.contactCard}
                >
                  <div style={styles.contactLabel}>WhatsApp</div>

                  <div style={styles.contactValue}>
                    {governanceInfo.canalTelefone}
                  </div>

                  <div style={styles.contactLinkHint}>
                    Clique para entrar em contato
                  </div>
                </a>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <div style={styles.footerLeftNote}>
                Informações institucionais do ambiente Logvi.
              </div>

              <div style={styles.footerActions}>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={() => setGovernanceModalOpen(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    position: "relative",
    width: "100%",
    minHeight: "100vh",
    overflow: "hidden",
    fontFamily:
      '"Nunito", "Inter", "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    background: "#0d1117",
    userSelect: "none",
    WebkitUserSelect: "none",
  },

  background: {
    position: "absolute",
    inset: 0,
    backgroundSize: "cover",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    transform: "none",
  },

  edgeBlur: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backdropFilter: "blur(5px)",
    WebkitBackdropFilter: "blur(5px)",
    WebkitMaskImage:
      "radial-gradient(ellipse at center, transparent 0%, transparent 48%, rgba(0,0,0,0.16) 62%, rgba(0,0,0,0.70) 80%, black 100%)",
    maskImage:
      "radial-gradient(ellipse at center, transparent 0%, transparent 48%, rgba(0,0,0,0.16) 62%, rgba(0,0,0,0.70) 80%, black 100%)",
  },

  topTint: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "linear-gradient(180deg, rgba(13,60,70,0.10) 0%, rgba(13,60,70,0.05) 12%, rgba(13,60,70,0.02) 24%, rgba(13,60,70,0) 36%)",
  },

  overlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(90deg, rgba(5,18,24,0.20) 0%, rgba(20,30,24,0.08) 35%, rgba(0,0,0,0.03) 100%)",
  },

  addressSelectorArea: {
    position: "fixed",
    top: 88,
    right: 25,
    zIndex: 8,
    width: "min(360px, calc(100vw - 60px))",
  },

  selectorSpacer: {
    height: 10,
  },

  addressSelectorShell: {
    position: "relative",
    width: "100%",
    borderRadius: 16,
    padding: "10px 12px",
    background: "rgba(0, 0, 0, 0.06)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    border: "1px solid rgba(255,255,255,0.10)",
    boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
    userSelect: "none",
    WebkitUserSelect: "none",
  },

  addressSelectorLabel: {
    marginBottom: 5,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.85,
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.70)",
    textAlign: "right",
    userSelect: "none",
    WebkitUserSelect: "none",
  },

  addressSelectorButton: {
    width: "100%",
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    gap: 8,
    textAlign: "right",
    color: "#ffffff",
  },

  addressSelectedLines: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    lineHeight: 1.12,
  },

  addressSelectedLine: {
    display: "block",
    width: "100%",
    color: "#ffffff",
    whiteSpace: "normal",
    wordBreak: "break-word",
    textShadow: "0 2px 10px rgba(0,0,0,0.45)",
  },

  addressSelectedLinePrimary: {
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 0.1,
  },

  addressSelectedLineSecondary: {
    marginTop: 2,
    fontSize: 11.5,
    fontWeight: 750,
    color: "rgba(255,255,255,0.88)",
  },

  addressSelectorChevron: {
    marginTop: 1,
    flexShrink: 0,
    color: "rgba(255,255,255,0.92)",
    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.35))",
    transition: "transform 160ms ease",
  },

  addressDropdown: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    width: "min(330px, calc(100vw - 60px))",
    maxHeight: 246,
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.10)",
    borderRadius: 12,
    boxShadow: "0 18px 38px rgba(15,23,42,0.18)",
    padding: 6,
    zIndex: 20,
  },

  addressDropdownEmpty: {
    padding: "9px 10px",
    color: "#6b7280",
    fontSize: 12,
    fontWeight: 600,
  },

  addressDropdownOption: {
    width: "100%",
    border: "none",
    background: "transparent",
    borderRadius: 10,
    padding: "8px 10px",
    margin: 0,
    cursor: "pointer",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
  },

  addressDropdownOptionSelected: {
    background: "rgba(18,66,128,0.09)",
  },

  addressDropdownLine: {
    display: "block",
    width: "100%",
    lineHeight: 1.18,
    whiteSpace: "normal",
    wordBreak: "break-word",
  },

  addressDropdownLinePrimary: {
    color: "#374151",
    fontSize: 12,
    fontWeight: 800,
  },

  addressDropdownLineSecondary: {
    color: "#7D8694",
    fontSize: 11,
    fontWeight: 600,
  },

  content: {
    position: "relative",
    zIndex: 2,
    minHeight: "100vh",
    padding: "20px 28px 28px 28px",
    boxSizing: "border-box",
  },

  customHeader: {
    height: 52,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    padding: "0 2px",
  },

  customHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  leftTopArea: {
    marginLeft: 10,
    marginTop: 40,
    width: "min(1000px, 100%)",
  },

  headerBlock: {
    marginBottom: 30,
  },

  title: {
    margin: 0,
    display: "flex",
    alignItems: "baseline",
    gap: 3,
    flexWrap: "wrap",
    lineHeight: 1.04,
    textShadow: "0 2px 12px rgba(0,0,0,0.22)",
  },

  titleHello: {
    fontSize: 20,
    fontWeight: 600,
    color: "#03e1f3",
    letterSpacing: 0,
  },

  titleName: {
    fontSize: 20,
    fontWeight: 600,
    color: "#ffffff",
    letterSpacing: 0,
  },

  subtitle: {
    margin: "6px 0 0 0",
    fontSize: 15,
    fontWeight: 500,
    lineHeight: 1.3,
    color: "rgba(255,255,255,0.9)",
    textShadow: "0 2px 14px rgba(0,0,0,0.20)",
  },

  modulesRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "stretch",
    marginBottom: 28,
  },

  moduleCard: {
    width: 122,
    minHeight: 118,
    border: "none",
    borderRadius: 12,
    padding: "14px 13px",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    cursor: "pointer",
    transition: "all 180ms ease",
    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
    textAlign: "left",
    overflow: "hidden",
  },

  moduleIcon: {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.95,
  },

  moduleTitle: {
    fontSize: 14,
    lineHeight: 1.28,
    fontWeight: 800,
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  },

  developmentBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    height: 18,
    padding: "0 6px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.20)",
    border: "1px solid rgba(255,255,255,0.28)",
    color: "#ffffff",
    fontSize: 9,
    fontWeight: 900,
    letterSpacing: 0.8,
    lineHeight: 1,
  },

  developmentPanel: {
    width: "min(480px, calc(100vw - 56px))",
    borderRadius: 6,
    padding: 18,
    background: "#111827",
    border: "1px solid #374151",
    boxShadow: "0 18px 42px rgba(0,0,0,0.24)",
    color: "#ffffff",
  },

  developmentPanelHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  developmentIconBox: {
    width: 42,
    height: 42,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#1f2937",
    boxShadow: "0 10px 22px rgba(0,0,0,0.22)",
    flexShrink: 0,
  },

  developmentTextGroup: {
    minWidth: 0,
  },

  developmentEyebrow: {
    marginBottom: 3,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "#9ca3af",
  },

  developmentTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.18,
    color: "#ffffff",
  },

  developmentDescription: {
    margin: "14px 0 0 0",
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1.48,
    color: "#d1d5db",
  },

  developmentFooter: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 16,
  },

  developmentCloseButton: {
    height: 34,
    padding: "0 14px",
    borderRadius: 4,
    border: "1px solid #374151",
    background: "#1f2933",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    transition: "all 160ms ease",
  },

  emptyState: {
    minWidth: 240,
    padding: "14px 16px",
    borderRadius: 6,
    color: "#ffffff",
    background: "#111827",
    border: "1px solid #374151",
    fontSize: 13,
    fontWeight: 500,
  },

  bottomActions: {
    position: "fixed",
    right: 24,
    bottom: 22,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    gap: 10,
    justifyContent: "flex-end",
  },

  governanceButton: {
    height: 40,
    padding: "0 14px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(15, 23, 32, 0.84)",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(0,0,0,0.24)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    transition: "all 160ms ease",
  },

  licenseButton: {
    height: 40,
    padding: "0 14px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(15, 23, 32, 0.84)",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(0,0,0,0.24)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    transition: "all 160ms ease",
  },

  licenseButtonIcon: {
    width: 18,
    height: 18,
    borderRadius: 999,
    background: "rgba(255,255,255,0.14)",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1,
    flexShrink: 0,
  },

  actionButtonText: {
    fontSize: 12.5,
    fontWeight: 800,
    letterSpacing: 0.1,
    color: "#ffffff",
    whiteSpace: "nowrap",
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background: "rgba(0,0,0,0.52)",
  },

  modalCard: {
    width: "min(720px, calc(100vw - 48px))",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    borderRadius: 4,
    background: "#ffffff",
    border: "1px solid #d1d5db",
    boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
    color: "#111827",
    padding: 22,
    fontFamily:
      '"Nunito", "Inter", "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
  },

  modalEyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 4,
  },

  modalTitle: {
    margin: 0,
    fontSize: 23,
    fontWeight: 900,
    lineHeight: 1.15,
    color: "#111827",
  },

  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 4,
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    color: "#111827",
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
    flexShrink: 0,
  },

  modalDivider: {
    height: 1,
    background: "#e5e7eb",
    marginBottom: 18,
  },

  assetSummary: {
    display: "grid",
    gridTemplateColumns: "96px minmax(0, 1fr)",
    gap: 16,
    alignItems: "center",
    padding: 14,
    borderRadius: 4,
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    marginBottom: 18,
  },

  assetThumbnailWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  assetThumbnail: {
    width: 96,
    height: 64,
    borderRadius: 4,
    backgroundSize: "cover",
    backgroundPosition: "center center",
    border: "1px solid #d1d5db",
  },

  assetSummaryText: {
    minWidth: 0,
  },

  assetTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#111827",
    lineHeight: 1.3,
    marginBottom: 4,
  },

  assetDescription: {
    fontSize: 13,
    fontWeight: 500,
    color: "#4b5563",
    lineHeight: 1.45,
  },

  sectionBlock: {
    marginBottom: 18,
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 0.2,
    color: "#111827",
    marginBottom: 10,
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  infoField: {
    borderRadius: 4,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    padding: "12px 12px",
  },

  infoFieldLabel: {
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 5,
  },

  infoFieldValue: {
    fontSize: 13.5,
    fontWeight: 700,
    lineHeight: 1.4,
    color: "#111827",
    wordBreak: "break-word",
  },

  textPanel: {
    borderRadius: 4,
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    padding: "12px 12px",
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1.55,
    color: "#374151",
  },

  contactGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  contactLinkHint: {
    marginTop: 6,
    fontSize: 11.5,
    fontWeight: 500,
    fontStyle: "italic",
    color: "#2563eb",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },

  contactCard: {
    display: "block",
    textDecoration: "none",
    borderRadius: 4,
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    padding: "12px 12px",
    color: "#111827",
  },

  contactLabel: {
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 5,
  },

  contactValue: {
    fontSize: 13.5,
    fontWeight: 800,
    lineHeight: 1.4,
    color: "#111827",
    wordBreak: "break-word",
  },

  modalFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginTop: 18,
    paddingTop: 16,
    borderTop: "1px solid #e5e7eb",
  },

  footerLeftNote: {
    fontSize: 12,
    color: "#6b7280",
    lineHeight: 1.4,
  },

  footerActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  secondaryButton: {
    height: 36,
    padding: "0 14px",
    borderRadius: 4,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#111827",
    fontSize: 12.5,
    fontWeight: 800,
    cursor: "pointer",
  },

  primaryButton: {
    height: 36,
    padding: "0 14px",
    borderRadius: 4,
    border: "1px solid #111827",
    background: "#111827",
    color: "#ffffff",
    fontSize: 12.5,
    fontWeight: 800,
    cursor: "pointer",
  },
};

export default Home;
